import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { appVersion } from "@/core/appVersion";
import welcomeIllustration from "@/assets/froppio-welcome.svg";
import { Brand } from "@/shared/components/ui/Brand";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { MarkdownDocument } from "@/shared/components/ui/MarkdownDocument";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import privacyPolicy from "../../../../../docs/legal/privacy-policy.md?raw";

interface LegalDocument {
  id: string;
  title: string;
  description: string;
  /** Inline content, or a loader for documents too large to ship in the main bundle. */
  content?: string | (() => Promise<string>);
}

const legalDocuments: LegalDocument[] = [
  { id: "privacy", title: "Privacy policy", description: "How your information is handled", content: privacyPolicy },
  { id: "terms", title: "Terms of service", description: "The terms for using Froppio" },
  {
    id: "licenses",
    title: "Open-source licenses",
    description: "The software behind Froppio",
    content: () => import("../../../../../docs/legal/open-source-licenses.md?raw").then((module) => module.default),
  },
];

type LoadedDocument = { id: string; content?: string; failed?: boolean };

function useLegalDocumentContent(legalDocument: LegalDocument | undefined) {
  const [loaded, setLoaded] = useState<LoadedDocument>();
  const source = legalDocument?.content;
  const id = legalDocument?.id;

  useEffect(() => {
    if (!id || typeof source !== "function") return;
    let active = true;
    source().then(
      (content) => active && setLoaded({ id, content }),
      () => active && setLoaded({ id, failed: true }),
    );
    return () => {
      active = false;
    };
  }, [id, source]);

  if (typeof source === "string") return { content: source, loading: false, failed: false };
  if (!source) return { content: undefined, loading: false, failed: false };
  const current = loaded?.id === id ? loaded : undefined;
  return { content: current?.content, loading: !current, failed: Boolean(current?.failed) };
}

export function AboutPage() {
  const { hash, pathname } = useLocation();
  const documentId = hash.slice(1);
  const legalDocument = legalDocuments.find(({ id }) => id === documentId);
  const { content: documentContent, loading, failed } = useLegalDocumentContent(legalDocument);
  const content = useRef<HTMLDivElement>(null);

  useEffect(() => {
    content.current?.focus();
  }, [hash]);

  return (
    <Page>
      <PageHeader title={legalDocument?.title ?? (documentId ? "Page not found" : "About")} />
      <PageContent className="max-w-[620px]">
        <div ref={content} tabIndex={-1} className="space-y-6 outline-none">
          {documentContent ? (
            <MarkdownDocument content={documentContent} />
          ) : loading ? (
            <LoadingSpinner message={`Loading ${legalDocument?.title.toLowerCase()}...`} />
          ) : documentId ? (
            <EmptyState
              title={failed ? "Document could not be loaded" : legalDocument ? "Document not available yet" : "Page not found"}
              message={failed
                ? `${legalDocument?.title} could not be loaded. Check your connection and try again.`
                : legalDocument
                ? `${legalDocument.title} content and an approved published link have not been provided. This page does not contain a legal document.`
                : "There is no About page at this address."}
              action={{ label: "About Froppio", to: pathname, variant: "outline" }}
            />
          ) : (
            <>
              <section aria-label="Froppio" className="grid justify-items-center gap-[18px] pb-3 pt-6 text-center">
                <Brand wordmark className="gap-2.5 [&>span:first-child]:text-[52px] [&>span:last-child]:text-[40px]" />
                <img
                  src={welcomeIllustration}
                  alt="A happy Froppio waving hello"
                  width={280}
                  height={240}
                  className="h-auto w-[200px] max-w-full"
                />
                <p className="text-sm text-muted-foreground">Version {appVersion}</p>
              </section>
              <Section title="Legal">
                <div className="space-y-2">
                  {legalDocuments.map(({ id, title, description, content }) => (
                    <InfoRow
                      key={id}
                      title={title}
                      subtitle={description}
                      caption={content ? undefined : "Document not available yet"}
                      href={`#${id}`}
                    />
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>
      </PageContent>
    </Page>
  );
}
